import { useAppDispatch } from '@/store/store-hooks'
import { useNavigate } from 'react-router-dom'
import postCreateAgentChat from '../fetch/post-create-agent-chat'
import { AgentConversationRead } from '@/agent/conversations/store/schemas'
import { AgentConversationsActions } from '@/agent/conversations/store/slice'
import { store } from '@/store/store'
import { selectMatterConversationsLoading } from '@/agent/conversations/store/selectors'
import { useCallback } from 'react'
import { buildConversationPath } from '@/chat-common/conversation-navigation/util/conversation-path-builders'
import { SkillsActions } from '@/skills/store/slice'
import { SkillSource } from '@/analytics/schema/events.schema'

/**
 * Create New Chat Hook
 * Hook to create a new chat conversation
 * @returns {createNewAgentChat} function to create a new chat
 */
export function useCreateNewAgentChat() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  /**
   * Create New Chat
   *
   * Creates a conversation on the server, puts it into state, and optionally navigates to it.
   *
   * @param navigateOnSuccess flag to indicate if we should navigate to the new chat after creation
   * @param matter_id matter id to associate the conversation with
   * @param onSuccess optional callback to call on success
   * @param onError optional callback to call on error
   */
  const createNewAgentChat = useCallback(
    async (navigateOnSuccess: boolean, matter_id: string | null, onSuccess?: () => void, onError?: (e: unknown) => void) => {
      try {
        // Ask the server for a new conversation
        const response: AgentConversationRead = await postCreateAgentChat(matter_id)

        // Insert it into state
        dispatch(AgentConversationsActions.upsertConversationFromRead(response))

        // A list fetch already in flight predates this create, so its response would drop the new
        // conversation. Force a refresh so a newer request id supersedes it; if nothing is in flight
        // the upsert alone suffices, so skip the refetch (PROD-687).
        if (selectMatterConversationsLoading(store.getState(), { matterId: response.matter_id })) {
          dispatch(AgentConversationsActions.refreshMatterConversationList({ matterId: response.matter_id, forceRefresh: true }))
        }

        // Call the onSuccess callback
        if (onSuccess) onSuccess()

        // If the navigateOnSuccess flag is set and we have a matter, navigate to the new chat
        if (navigateOnSuccess && matter_id) {
          navigate(buildConversationPath(matter_id, response.id))
        }

        return response
      } catch (e) {
        // Call the onError callback
        if (onError) onError(e)
      }
    },
    [dispatch, navigate]
  )

  /**
   * Create New Chat with Skill
   *
   * Handles:
   * 1. Create a new chat
   * 2. Initialize the conversation with the skill
   * 3. Navigate to the new chat -> Always true unless otherwise specified
   *
   * @param navigateOnSuccess flag to indicate if we should navigate to the new chat after creation
   * @param matter_id matter id to associate the conversation with
   * @param skill the skill to initialize the conversation with
   */
  const createNewAgentChatWithSkill = async (
    navigateOnSuccess = true,
    matter_id: string | null,
    skill: string,
    skillSource: SkillSource = SkillSource.quick_action
  ) => {
    // TODO: To improve and optimize, check if NEW chat exists and if so, use it and update the skill instead of creating a new one
    const response = await createNewAgentChat(navigateOnSuccess, matter_id)
    if (response) {
      dispatch(
        SkillsActions.initializeConversationWithSkill({
          conversationId: response.id,
          skill: skill,
          skillSource: skillSource,
        })
      )
    }
  }

  return { createNewAgentChat, createNewAgentChatWithSkill }
}
